import { yupResolver } from "@hookform/resolvers/yup";
import {
  formatOrderCostMoney,
  fromOrderCostThousandUnit,
  parseOrderCostMoney,
} from "helpers/orderCostHelper";
import {
  buildScanCaptureFileName,
  convertCaptureDataUriToFile,
} from "pages/TicketManagement/ticketMediaUtils";
import { useAddReceiveBusChild, useUpdateOrderCost } from "hooks";
import { useUploadResource } from "hooks/import-management/useImport";
import {
  useOrderScanRemoveFile,
  useOrderScanUploadFile,
} from "hooks/order-scan/useOrderScan";
import TicketBarcodeInput from "pages/TicketManagement/TicketBarcodeInput";
import ReactCamera from "pages/ ScanTheRow/ReactCamera/ReactCamera";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useToasts } from "react-toast-notifications";
import {
  Button,
  Col,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as yup from "yup";

/** Modal thêm đơn con đơn lấy hàng nhà xe (quét ScanRow hoặc mở thủ công). */
const AddReceiveBusChildModal = ({
  parentId,
  scanSessionId,
  initialBarcode = "",
  isOpen,
  toggle,
  onSuccessHandle,
}) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);
  const [errorMessage, setErrorMessage] = useState("");
  const [listImageCapture, setListImageCapture] = useState([]);
  const [hoveredCaptureId, setHoveredCaptureId] = useState(null);
  const [lastAddedChildOrderId, setLastAddedChildOrderId] = useState(null);
  const webcamRef = useRef(null);
  const cancelledCaptureIdsRef = useRef(new Set());

  const schema = yup.object().shape({
    child_barcode: yup
      .string()
      .trim()
      .required("Vui lòng nhập mã đơn hoặc mã tham chiếu"),
    cost_value: yup
      .string()
      .nullable()
      .transform(value => value ?? "")
      .test("cost-positive-if-provided", "Chi phí phải lớn hơn 0", value => {
        const trimmed = String(value || "").trim();

        if (!trimmed) {
          return true;
        }

        const digits = parseOrderCostMoney(trimmed);

        return Number(digits) > 0;
      }),
  });

  const mainForm = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      child_barcode: "",
      cost_value: "",
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    clearErrors,
    setValue,
    formState: { errors },
  } = mainForm;

  const clearFormInputs = useCallback(
    (barcode = "") => {
      reset({
        child_barcode: barcode,
        cost_value: "",
      });
      clearErrors();
      setErrorMessage("");
      setListImageCapture([]);
      setHoveredCaptureId(null);
      cancelledCaptureIdsRef.current = new Set();
    },
    [reset, clearErrors]
  );

  const focusBarcodeInput = () => {
    setTimeout(() => {
      document.getElementById("add-receive-bus-child-barcode")?.focus();
    }, 0);
  };

  const focusCostInput = () => {
    setTimeout(() => {
      document.getElementById("add-receive-bus-child-cost")?.focus();
    }, 0);
  };

  const { mutate: mutateOrderScanUploadFile } = useOrderScanUploadFile({
    onSuccess: () => {},
    onError: () => {},
  });
  const { mutate: mutateOrderScanRemoveFile } = useOrderScanRemoveFile({
    onSuccess: () => {},
    onError: () => {},
  });

  const handleUploadImageSuccess = (captureId, response) => {
    const resourceId = response?.data?.id;

    if (!resourceId) {
      return;
    }

    if (cancelledCaptureIdsRef.current.has(captureId)) {
      mutateOrderScanRemoveFile({ listIdResource: [resourceId] });
      return;
    }

    setListImageCapture(prev => {
      const updatedList = prev.map(item =>
        item.captureId === captureId ? { ...item, idResouce: resourceId } : item
      );

      if (scanSessionId) {
        const listIdResource = updatedList
          .map(item => item.idResouce)
          .filter(Boolean);

        if (listIdResource.length) {
          mutateOrderScanUploadFile({
            idHistoryScan: scanSessionId,
            listIdResource,
          });
        }
      }

      return updatedList;
    });
  };

  const { mutate: mutateUploadImage } = useUploadResource(
    () => {},
    () => {}
  );

  const handleCaptureImage = srcImage => {
    const captureId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const data = new FormData();
    const fileName = buildScanCaptureFileName();

    data.append("file", convertCaptureDataUriToFile(srcImage, fileName));
    setListImageCapture(prev => [
      ...prev,
      {
        captureId,
        srcImage,
        fileName,
        idResouce: null,
      },
    ]);
    mutateUploadImage(data, {
      onSuccess: response => {
        handleUploadImageSuccess(captureId, response);
      },
    });
  };

  const handleRemoveImage = item => {
    if (item?.captureId) {
      cancelledCaptureIdsRef.current.add(item.captureId);
    }

    setListImageCapture(prev =>
      prev.filter(image => image.captureId !== item?.captureId)
    );

    if (item?.idResouce) {
      mutateOrderScanRemoveFile({
        listIdResource: [item.idResouce],
      });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setLastAddedChildOrderId(null);
    clearFormInputs(String(initialBarcode || "").trim());
  }, [isOpen, initialBarcode, clearFormInputs]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = event => {
      if (!event.altKey || event.code !== "KeyQ") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const barcodeInput = document.getElementById(
        "add-receive-bus-child-barcode"
      );
      const costInput = document.getElementById("add-receive-bus-child-cost");
      const activeElement = document.activeElement;

      if (activeElement === barcodeInput) {
        focusCostInput();
        return;
      }

      if (activeElement === costInput) {
        focusBarcodeInput();
        return;
      }

      focusCostInput();
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    toggle?.();
  };

  const resolveAddedChildOrderId = response => {
    const possibleOrderId = [
      response?.data?.id,
      response?.data?.order_id,
      response?.data?.order?.id,
      response?.id,
      response?.order_id,
      response?.order?.id,
    ].find(value => Number.isFinite(Number(value)) && Number(value) > 0);

    return possibleOrderId ? Number(possibleOrderId) : null;
  };

  const { mutate: mutateUpdateOrderCost, isLoading: isUpdatingCost } =
    useUpdateOrderCost(() => {
      // Clear cost after successful update to avoid applying old value
      // to the next scanned child order.
      setValue("cost_value", "");
      focusBarcodeInput();
    });

  const onMutationSuccess = response => {
    if (response?.result) {
      const nextOrderId = resolveAddedChildOrderId(response);

      if (nextOrderId) {
        setLastAddedChildOrderId(nextOrderId);
      }

      onSuccessHandle?.();
      webcamRef?.current?.captureImage();
      clearFormInputs();
      focusBarcodeInput();
      return;
    }

    const message = response?.message;

    if (typeof message === "string") {
      setErrorMessage(message);
      return;
    }

    if (message && typeof message === "object") {
      const fields = Object.keys(message);
      const combinedMessage = fields
        .map(field => message[field]?.[0])
        .filter(Boolean)
        .join(", ");

      setErrorMessage(
        combinedMessage ||
          t("message.addReceiveBusChild", { status: t("message.error") })
      );
      return;
    }

    addToast(t("message.addReceiveBusChild", { status: t("message.error") }), {
      appearance: "error",
      autoDismiss: true,
    });
  };

  const { mutate: mutateAddReceiveBusChild, isLoading: isSubmitting } =
    useAddReceiveBusChild(onMutationSuccess);

  const handleBarcodeEnter = event => {
    if (event.key.toLowerCase() !== "enter") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    handleSubmit(onSubmit)();
  };

  const handleCostKeyDown = event => {
    if (event.key.toLowerCase() !== "enter") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (!lastAddedChildOrderId) {
      addToast(t("message.noOrderToUpdateCost"), {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    const costDigits = parseOrderCostMoney(mainForm.getValues("cost_value"));

    if (!costDigits) {
      addToast("Vui lòng nhập chi phí trước khi cập nhật", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    mutateUpdateOrderCost({
      orderId: lastAddedChildOrderId,
      cost_value: fromOrderCostThousandUnit(costDigits),
    });
  };

  const onSubmit = values => {
    const costDigits = parseOrderCostMoney(values.cost_value);
    const payload = {
      parent_id: parentId,
      child_barcode: String(values.child_barcode || "").trim(),
    };

    if (costDigits) {
      payload.cost_value = fromOrderCostThousandUnit(costDigits);
    }

    mutateAddReceiveBusChild(payload);
  };

  const isAllowCapture = true;

  return (
    <Modal
      size="lg"
      role="dialog"
      autoFocus={false}
      backdrop="static"
      centered
      isOpen={isOpen}
      toggle={handleToggle}
      onOpened={focusBarcodeInput}
    >
      <ModalHeader toggle={handleToggle}>Thêm đơn con</ModalHeader>
      <ModalBody>
        <Row className="align-items-start">
          <Col md={7} xs={12}>
            <Controller
              control={control}
              name="child_barcode"
              render={({ field }) => (
                <TicketBarcodeInput
                  id="add-receive-bus-child-barcode"
                  label="Mã đơn / Mã tham chiếu"
                  value={field.value}
                  onChange={field.onChange}
                  onKeyDown={handleBarcodeEnter}
                  placeholder="Quét hoặc nhập mã"
                  isRequired
                  invalid={Boolean(errors.child_barcode)}
                  errorMessage={errors.child_barcode?.message}
                />
              )}
            />
          </Col>
          <Col md={5} xs={12}>
            <Label className="form-label">Hình ảnh</Label>
            <ReactCamera
              ref={webcamRef}
              isAllowCapture={isAllowCapture}
              handleCaptureImage={handleCaptureImage}
            />
          </Col>
        </Row>

        <Row className="align-items-start mt-3">
          <Col md={7} xs={12}>
            <Label
              htmlFor="add-receive-bus-child-cost"
              className="form-label d-flex align-items-center gap-1"
            >
              Chi phí
              <sup className="text-info mb-0" style={{ fontStyle: "italic" }}>
                (ALT + Q)
              </sup>
            </Label>
            <Controller
              control={control}
              name="cost_value"
              render={({ field }) => (
                <input
                  id="add-receive-bus-child-cost"
                  type="text"
                  inputMode="numeric"
                  className={`form-control ${
                    errors.cost_value ? "is-invalid" : ""
                  }`}
                  placeholder="Nhập chi phí (không bắt buộc)"
                  value={formatOrderCostMoney(field.value)}
                  onChange={event => {
                    field.onChange(parseOrderCostMoney(event.target.value));
                  }}
                  onKeyDown={handleCostKeyDown}
                />
              )}
            />
            {errors.cost_value?.message ? (
              <div className="invalid-feedback d-block">
                {errors.cost_value.message}
              </div>
            ) : null}
          </Col>
          <Col md={5} xs={12}>
            {!isAllowCapture ? (
              <p className="text-muted small mb-2" style={{ fontStyle: "italic" }}>
                Quét đơn cha trước để chụp ảnh gắn phiên quét.
              </p>
            ) : null}
            {listImageCapture.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  maxHeight: 120,
                  overflowY: "auto",
                }}
              >
                {listImageCapture.map(item => {
                  const isHovered = hoveredCaptureId === item.captureId;

                  return (
                    <div
                      key={item.captureId}
                      onMouseEnter={() => setHoveredCaptureId(item.captureId)}
                      onMouseLeave={() => setHoveredCaptureId(null)}
                      style={{
                        position: "relative",
                        width: 72,
                        height: 72,
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={item.srcImage}
                        alt="Captured"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 4,
                          opacity: isHovered ? 0.45 : 1,
                        }}
                      />
                      <i
                        className="fas fa-trash fa-lg text-danger"
                        title="Xóa hình ảnh"
                        role="button"
                        onClick={() => handleRemoveImage(item)}
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          cursor: "pointer",
                          opacity: isHovered ? 1 : 0,
                          pointerEvents: isHovered ? "auto" : "none",
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            ) : null}
          </Col>
        </Row>

        {errorMessage ? (
          <div className="d-flex gap-1 align-items-center mt-2">
            <i className="bx bx-error text-danger" style={{ fontSize: "17px" }} />
            <div className="text-danger">{errorMessage}</div>
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button
          type="button"
          color="danger"
          className="me-2 btn-label"
          disabled={isSubmitting}
          onClick={handleToggle}
        >
          <i className="bx bx-x label-icon" />
          Đóng
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddReceiveBusChildModal.propTypes = {
  parentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  scanSessionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  initialBarcode: PropTypes.string,
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  onSuccessHandle: PropTypes.func,
};

export default AddReceiveBusChildModal;
